import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '@/shared/ui/PageHeader'
import { Card } from '@/shared/ui/Card'
import { Avatar } from '@/shared/ui/Avatar'
import { Spinner } from '@/shared/ui/Spinner'
import { DirhamAmount } from '@/shared/ui/Dirham'
import { useWalletStudents } from '@/api/hooks'
import { useDebounce } from '@/shared/hooks'
import { localized } from '@/shared/utils'
import { Search, ChevronLeft, ChevronRight, PenSquare, History } from 'lucide-react'
import { EditWalletBalanceModal, type WalletStudent } from '@/features/wallet/EditWalletBalanceModal'
import { WalletHistoryModal } from '@/features/wallet/WalletHistoryModal'

const PAGE_SIZE = 20

export function StudentWalletPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [editingStudent, setEditingStudent] = useState<WalletStudent | null>(null)
  const [historyStudent, setHistoryStudent] = useState<WalletStudent | null>(null)
  const debounced = useDebounce(search, 350)

  useEffect(() => { setPage(1) }, [debounced])

  const listQ = useWalletStudents({ wallet_filter: 'positive', search: debounced || undefined, page, limit: PAGE_SIZE })
  const rows = listQ.data?.data ?? []
  const total = listQ.data?.total ?? 0
  const perPage = listQ.data?.count_per_page ?? PAGE_SIZE
  const lastPage = Math.max(1, Math.ceil(total / perPage))

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('studentWallet.title')}
        subtitle={t('studentWallet.subtitle')}
        breadcrumbs={[{ label: t('nav.dashboard'), to: '/dashboard' }, { label: t('studentWallet.title') }]}
      />

      <Card padding={false}>
        <div className="p-4 border-b border-gray-100">
          <div className="relative max-w-sm">
            <Search size={15} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-gray-400"/>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('studentWallet.searchPlaceholder')}
              aria-label={t('studentWallet.searchPlaceholder')}
              className="w-full h-10 ps-9 pe-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-400/30"
            />
          </div>
        </div>

        {listQ.isLoading ? (
          <div className="flex justify-center py-16"><Spinner className="w-8 h-8"/></div>
        ) : listQ.isError ? (
          <div className="text-center text-sm text-red-500 py-16">{t('studentWallet.loadError')}</div>
        ) : rows.length === 0 ? (
          <div className="text-center text-sm text-gray-400 py-16">{t('studentWallet.emptyState')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="drivys-table w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th>{t('studentWallet.colStudent')}</th>
                  <th>{t('studentWallet.colPhone')}</th>
                  <th>{t('studentWallet.colBalance')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => {
                  const name = localized(u, 'name', i18n.language)
                  return (
                    <tr key={u.id} className="cursor-pointer" onClick={() => navigate(`/dashboard/user/${u.id}`)}>
                      <td>
                        <div className="flex items-center gap-2.5">
                          <Avatar name={name} src={u.photo_url} size="sm"/>
                          <span className="font-semibold text-gray-800">{name}</span>
                        </div>
                      </td>
                      <td className="text-gray-600 whitespace-nowrap">{u.phone ?? '—'}</td>
                      <td><DirhamAmount value={u.wallet_balance} size={13}/></td>
                      <td>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); setEditingStudent(u) }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-brand-600 border border-brand-200 rounded-lg hover:bg-brand-50 transition-colors"
                          >
                            <PenSquare size={12}/> {t('studentWallet.adjustBalance')}
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setHistoryStudent(u) }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <History size={12}/> {t('studentWallet.history.viewLink')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {total > perPage && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 flex-wrap gap-2">
            <span className="text-xs text-gray-400">
              {t('studentWallet.range', { start: (page - 1) * perPage + 1, end: Math.min(page * perPage, total), total })}
            </span>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className="flex items-center gap-1 px-3 py-1.5 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 text-gray-600 font-medium">
                <ChevronLeft size={13} className="rtl:rotate-180"/> {t('common.pagination.previous')}
              </button>
              <span className="text-xs text-gray-400">{page} / {lastPage}</span>
              <button onClick={() => setPage(p => Math.min(lastPage, p + 1))} disabled={page >= lastPage}
                className="flex items-center gap-1 px-3 py-1.5 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 text-gray-600 font-medium">
                {t('common.pagination.next')} <ChevronRight size={13} className="rtl:rotate-180"/>
              </button>
            </div>
          </div>
        )}
      </Card>

      <EditWalletBalanceModal
        open={!!editingStudent}
        onClose={() => setEditingStudent(null)}
        student={editingStudent}
      />
      <WalletHistoryModal
        open={!!historyStudent}
        onClose={() => setHistoryStudent(null)}
        student={historyStudent}
      />
    </div>
  )
}
